import { ACClickEventDetails, SearchResults } from "../Shared/scriptts/interfaces";
import { getPrintableCharacters, isEmpty, getBoolean } from "../Shared/scriptts/utilities";
import usaj_ga from "../Shared/script/Analytics/usaj-analytics";
import { Params } from "../Shared/scriptts/search-params";
import { URLCriteriaHelper } from "./search-urlcriteriahelper";
import { buildSearchResultsCards } from "./Templates/search-results-card";
import { getSearchResultsPagination, bindPageButtonEvents } from "./Templates/search-results-pagination";
import { displaySortFilter } from './Templates/search-filter-sort';
import { displayFilters, displayProfilePreferenceToggle, initBrandedSearch, initUserPreferences, addParam } from './search-filters-main';
import { displaySaveSearchForm, parseSavedSearchParams } from './search-save-main';
import { displayFilterPills } from "./Templates/search-filter-pills";
import { setUserObject } from "../Shared/scriptts/session/sessionmain";

declare var gAliasMapping: any;
declare var gSearchFilters: any;
declare var gBrandedSearch: any;
const executeSavedSearchUrl = '/Search/ExecuteStoredSearch';
export const executeSearchUrl = '/Search/ExecuteSearch';
export let params: Params;
export const aliasMapping = gAliasMapping;
export let searchFilters = gSearchFilters;
export const brandedSearch = gBrandedSearch;

window["getQueryParams"] = function () {
    return JSON.stringify(params);
}

document.addEventListener('session-loaded', () => {
    params = URLCriteriaHelper.ParseURL(params);
    initBrandedSearch();
    initUserPreferences();
    if (params.SavedSearchID !== undefined && params.SavedSearchID !== null) {
        displayResults(executeSavedSearchUrl + `?SavedSearchID=${params.SavedSearchID?.toString()}`, false, true);
    }
    else {
        displayResults(executeSearchUrl, false, true);
    }
    displayProfilePreferenceToggle();
    document.addEventListener('acclick', autocompleteClicked);
    document.querySelector<HTMLButtonElement>('#jobs-search')?.addEventListener('click', searchClicked);
});

/**
 * Returns a boolean value that determines if the current search has a keyword or filter applied
 * @returns True if search has a keyword or filter, False otherwise
 */
export function hasKeywordOrFilter(): boolean {
    for (const key in params) {
        if (key in aliasMapping && aliasMapping[key].SearchFilter) {
            if (params[key] instanceof Array) {
                if (params[key].filter(p => p.toString() !== '').length > 0) return true;
            } else if (params[key] !== undefined && params[key] !== null && params[key].toString() !== '') {
                return true;
            }
        }
    }
    return false;
}

/**
 * Displays the search results based on user-specified keyword and filters
 * @param searchUrl The URL of the search API endpoint to use
 * @param giveFocusToTop If the top of the results should receive focus once results are displayed
 * @param rebuildFilters If the search filters should be rebuilt
 */
export async function displayResults(searchUrl: string, giveFocusToTop = false, rebuildFilters = false) {
    const keywordInput = document.querySelector<HTMLInputElement>('#nav-keyword');
    const searchHeader = document.querySelector<HTMLElement>('#search-header');
    const filterCountHeader = document.querySelector<HTMLElement>('#filter-job-count');
    const resultsElem = document.querySelector<HTMLElement>('#search-results');
    const pagerElem = document.querySelector<HTMLElement>('#search-results-pagination');
    if (!keywordInput || !searchHeader || !resultsElem || !pagerElem || !filterCountHeader) {
        return;
    }
    const loadingElem = document.querySelector<HTMLElement>('#loading');
    const loadingTimer = setTimeout(() => loadingElem?.classList.remove('hidden'), 500);
    let results = <SearchResults>{};
    
    params.Keyword = (params.Keyword !== undefined && params.Keyword !== null) ? getPrintableCharacters(params.Keyword) : params.Keyword;
    params.LocationName = params.LocationName?.filter(location => location.trim() !== '').map(location => getPrintableCharacters(location));
    
    keywordInput.value = params.Keyword ?? '';
    resultsElem.innerHTML = '';
    pagerElem.innerHTML = '';

    URLCriteriaHelper.reconstructUrl(true);

    try {
        const response = await fetch(searchUrl, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json; charset=utf-8"
            },
            cache: "no-cache",
            body: JSON.stringify(params)
        });
        if (!response.ok) {
            console.error(response.statusText);
        }
        const data = await response.json();
        const isSavedSearch = searchUrl.includes(executeSavedSearchUrl);
        if (!isSavedSearch && !hasKeywordOrFilter()) {
            searchHeader.innerText = '';
            filterCountHeader.innerText = '';
            document.querySelector<HTMLElement>('#no-params')?.classList.remove('hidden');
            displaySaveSearchForm(false);
            displayFilters(data);
            displaySortFilter(false);
        }
        else {
            document.querySelector<HTMLElement>('#no-params')?.classList.add('hidden');
            if (isSavedSearch) {
                results = <SearchResults>data.SearchResult;
                parseSavedSearchParams(data['SavedSearchParams']);
                URLCriteriaHelper.reconstructUrl();
            }
            else {
                results = <SearchResults>data;
                params = URLCriteriaHelper.ParseURL(params);
            }
            if (results.Total > 0) {
                const first = (results.Pager.CurrentPageIndex - 1) * results.Pager.ItemsPerPage + 1;
                const last = results.Pager.CurrentPageIndex * results.Pager.ItemsPerPage;

                searchHeader.innerText = `Viewing ${first}-${last > results.Total ? results.Total : last} of ${results.Total} jobs`;
                filterCountHeader.innerText = results.Total === 0 ? '0 jobs' : results.Total > 1 ? `${results.Total} jobs` : '1 job';
                buildSearchResultsCards(results);
                pagerElem.innerHTML = getSearchResultsPagination(results.Pager);
                bindPageButtonEvents(pagerElem);
                if (giveFocusToTop === true) {
                    resultsElem.querySelector('a')?.focus();
                }
                document.querySelector<HTMLElement>('#no-search-results')?.classList.add('hidden');
            }
            else {
                if (searchHeader !== null) {
                    searchHeader.classList.remove('hidden');
                    searchHeader.setAttribute('aria-hidden', 'false');
                    searchHeader.innerText = !isEmpty(params.Keyword) ? `No jobs found for "${params.Keyword?.trim()}"` : 'No jobs found';
                }

                resultsElem.innerHTML = '';
                pagerElem.innerHTML = '';
                document.querySelector<HTMLElement>('#no-search-results')?.classList.remove('hidden');
            }
            if (rebuildFilters === true) {
                displayFilters(results);
            }
            else {
                displayFilterPills();
            }
            displaySaveSearchForm(true);
            displaySortFilter();
        }
        GALogSearch(results);
    }
    catch (err) {
        return console.error('Error', err);
    }
    finally {
        loadingElem?.classList.add('hidden');
        clearTimeout(loadingTimer);
    }
}

export function setParams(value: Params) {
    params = value;
}

export function signinClick() {
    const searchParameters = window.location.search != "" ? window.location.search.replace(/([?&])(p=\d*)/ig, "$1p=1") + "&auth=True" : "?auth=True";
    location.href = window.location.protocol + "//" + window.location.host + "/Account/Challenge/?returnPath=/Search/Results" + searchParameters;
}

function GALogSearch(data: SearchResults) {
    let userObject = JSON.parse(sessionStorage.getItem('userObject') || "{}");
    let isAutocomplete = getBoolean(sessionStorage.getItem('autocomplete'));

    sessionStorage.removeItem('autocomplete');

    if (!userObject.UniqueSearch) {
        userObject.UniqueSearch = {
            Id: crypto.randomUUID(),
            Keyword: params.Keyword,
            Page: params.Page,
            filters: {},
            total_results: params.TotalResults
        };
        setUserObject(userObject);
    }
    if (userObject) {
        params.IsAuthenticated = userObject.IsAuthenticated;
        params.TotalResults = data.Total;
        if (isEmpty(params.Keyword?.toString()) || (userObject.UniqueSearch && params.Keyword?.toLowerCase() === userObject.UniqueSearch.Keyword?.toLowerCase())) {
            params.UniqueSearchID = userObject.UniqueSearch.Id;
            usaj_ga.log_search_filter_interaction(params, isAutocomplete);
        }
        else {
            userObject.UniqueSearch.Id = crypto.randomUUID();
            userObject.UniqueSearch.Keyword = params.Keyword;
            userObject.UniqueSearch.Page = params.Page;
            params.UniqueSearchID = userObject.UniqueSearch.Id;
            setUserObject(userObject);
            usaj_ga.log_search(params, isAutocomplete);
        }
    }
}

function autocompleteClicked(evt: Event) {
    const detail = <ACClickEventDetails>(evt['detail']);
    if (!detail || isEmpty(detail.ParamProperty) || isEmpty(detail.Code)) {
        return;
    }

    addParam(detail.ParamProperty, detail.Code);
    displayResults(executeSearchUrl, true, true);
}

function searchClicked(evt: Event) {
    evt.preventDefault();

    const keywordElem = document.querySelector<HTMLInputElement>('#nav-keyword');
    const locationElem = document.querySelector<HTMLInputElement>('#nav-location');

    if (!keywordElem || !locationElem || (isEmpty(keywordElem.value) && isEmpty(locationElem.value))) {
        return;
    }

    if (!isEmpty(keywordElem.value)) {
        addParam('Keyword', getPrintableCharacters(keywordElem.value));
    }
    if (!isEmpty(locationElem.value)) {
        addParam('LocationName', getPrintableCharacters(locationElem.value));
    }
    displayResults(executeSearchUrl, true, true);
}
